import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

/**
 * The Carrier Quoting agent's screens. A quote is one household shopping for
 * auto cover: its paperwork read into a customer file, the carriers asked,
 * and their answers lined up into a proposal.
 */

export type CqStatus = 'reading' | 'review' | 'quoting' | 'ready' | 'failed';
export type CqCarrierStatus = 'queued' | 'needs_you' | 'quoted' | 'referred' | 'declined';
export type CqKind = 'dec_page' | 'application' | 'license' | 'registration' | 'unknown';
export type CqPackageKey = 'current' | 'recommended' | 'higher';

export interface CqQuoteCard {
  id: string;
  created_at: string;
  quoted_at: string | null;
  by: string | null;
  status: CqStatus;
  /** Where it is, in words: "Asking the carriers", "Proposal ready". */
  stage: string;
  insured: string | null;
  state: string | null;
  drivers: number;
  vehicles: number;
  current_carrier: string | null;
  current_premium: number | null;
  best_carrier: string | null;
  best_premium: number | null;
  /** Against today's policy for the same cover, for the term. */
  saving: number | null;
  document_count: number;
  carriers_asked: number;
  carriers_quoted: number;
  needs_you: boolean;
  error: string | null;
  source: 'live' | 'seed';
}

export interface CqDocument {
  id: string;
  filename: string;
  kind: CqKind;
  kind_label: string;
  status: 'uploaded' | 'read' | 'failed';
  /** Picked up by the agent and being read right now. */
  reading: boolean;
  /** How long the reading took. */
  seconds: number | null;
  pages: number | null;
  size_bytes: number;
  is_picture: boolean;
  error: string | null;
  /** What was read from it, as a few lines to check against the page. */
  facts: { label: string; value: string }[];
  uploaded_at: string;
}

export interface CqFileRow { label: string; value: string | null; source: string | null }
export interface CqFileSection { title: string; rows: CqFileRow[] }

export interface CqMissing {
  key: string;
  label: string;
  kind: 'text' | 'date' | 'number' | 'choice';
  /** Without it no carrier can price the household. */
  needed: boolean;
  why: string;
  options?: string[];
}

export interface CqCoverage {
  code: string;
  name: string;
  limits: number[];
  deductible: number | null;
  premium: number;
}

export interface CqPackage {
  total: number | null;
  vehicles: { index: number; coverages: CqCoverage[]; subtotal: number }[];
  discounts: { code: string; name: string; percent: number; amount: number }[];
  fees: { code: string; name: string; amount: number }[];
  pay_plans: { code: string; name: string; down: number; payments: number; each: number; total: number; note?: string }[];
  available: { name: string; detail: string }[];
  notes: string[];
}

export interface CqCarrier {
  id: string;
  carrier: string;
  name: string;
  company: string | null;
  naic: string | null;
  program: string | null;
  route: 'rater' | 'portal';
  /** What it was asked through: the rater, or the carrier's own website. */
  via: string;
  /** The agency's producer code with this carrier. */
  login: string;
  status: CqCarrierStatus;
  /** What is happening right now, while it has not answered. */
  step: string | null;
  sent_at: string | null;
  answered_at: string | null;
  seconds: number | null;
  quote_number: string | null;
  premium: number | null;
  term_months: number | null;
  valid_until: string | null;
  effective: string | null;
  reason: string | null;
  code: string | null;
  portal: { submitted: boolean; screens?: { name: string; fields: number }[] } | null;
  has_messages: boolean;
  packages: Partial<Record<CqPackageKey, CqPackage>>;
  discount_total: number;
}

export interface CqCell { text: string; differs: boolean }
export interface CqTable {
  key: CqPackageKey;
  name: string;
  columns: { key: string; label: string; sub: string }[];
  rows: { group: string; key: string; label: string; cells: Record<string, CqCell> }[];
}

export interface CqRecommendation {
  carrier: string;
  carrier_name: string;
  package?: CqPackageKey;
  package_name?: string;
  premium: number;
  term_months: number;
  today: number | null;
  same_cover: number | null;
  saving_same_cover: number | null;
  reasons: string[];
}

export interface CqOption {
  package: CqPackageKey; name: string; carrier_name: string; premium: number;
  against_today: number | null; recommended: boolean;
}

/** What is on the proposal, what else could be, and the email it goes out with. */
export interface CqProposal {
  recommendation: CqRecommendation;
  options: CqOption[];
  /** False once a person has chosen something other than the rules' pick. */
  by_rule: boolean;
  rule: { carrier: string; carrier_name: string };
  carriers: { carrier: string; name: string; premiums: Partial<Record<CqPackageKey, number>> }[];
  packages: { key: CqPackageKey; name: string }[];
  email: { to: string; subject: string; body: string } | null;
  sent: { to: string; subject: string; by: string | null; at: string }[];
}

export interface CqComparison {
  packages: { key: CqPackageKey; name: string }[];
  tables: Partial<Record<CqPackageKey, CqTable>>;
  premiums: Partial<Record<CqPackageKey, Record<string, number>>>;
  flags: { severity: 'important' | 'note'; title: string; detail: string }[];
  recommendation: CqRecommendation | null;
  options: CqOption[];
  counts: { asked: number; quoted: number; referred: number; declined: number; waiting: number };
}

export interface CqQuoteDetail extends CqQuoteCard {
  agency: string;
  rater: string;
  /** The file the rater's logo is kept under. */
  rater_logo: string;
  documents: CqDocument[];
  file: CqFileSection[];
  vehicle_names: string[];
  missing: CqMissing[];
  can_confirm: boolean;
  approved_by: string | null;
  approved_at: string | null;
  carriers: CqCarrier[];
  comparison: CqComparison | null;
  proposal: CqProposal | null;
}

export interface CqMessages {
  carrier: string;
  route: 'rater' | 'portal';
  format: string;
  request: string | null;
  response: string | null;
  sent_at: string | null;
  answered_at: string | null;
}

export interface CqDay { day: string; value: number }

export interface CqSpotlight {
  rater: string;
  rater_logo: string;
  carriers: { key: string; name: string; route: 'rater' | 'portal' }[];
  flow: {
    last_run_at: string | null; error: string | null;
    reading_now: number; waiting_for_you: number; quoting_now: number;
  };
  tiles: {
    quotes: CqDay[]; ready: CqDay[];
    totals: {
      households: number;
      proposals: number;
      carriers_asked: number;
      carrier_quotes: number;
      average_saving: number | null;
      average_seconds: number | null;
    };
  };
  answers: { label: string; value: number }[];
  won: { label: string; value: number }[];
}

export interface CqConfiguration {
  carriers: {
    key: string; name: string; company: string; naic: string; program: string;
    route: 'rater' | 'portal'; route_label: string; producer_code: string; rater_logo: string;
    terms: string; telematics: string;
  }[];
  packages: { name: string; detail: string }[];
  reads: { kind: string; label: string; detail: string }[];
  flags: { label: string; severity: string }[];
  rules: { when: string; then: string; detail: string }[];
  guardrails: { title: string; detail: string }[];
  limits: { max_files: number; max_mb: number; accepted: string };
}

export interface CqIntegration {
  id: string;
  name: string;
  role: string;
  connected: boolean;
  /** The file its logo is kept under, when it has one. */
  logo?: string;
  detail?: string;
  checked_at?: string | null;
  error?: string | null;
}

function authHeaders(json = true): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return { ...(json && { 'Content-Type': 'application/json' }), ...(token && { Authorization: `Bearer ${token}` }) };
}

function prefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return `${tenantApiPrefix(tid)}/carrier-quoting`;
}

async function call<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const response = await fetch(buildApiUrl(`${prefix()}${path}`), { ...init, headers: authHeaders() });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallback);
  }
  return response.status === 204 ? (undefined as T) : response.json();
}

export const fetchQuotes = () => call<{ quotes: CqQuoteCard[] }>('/quotes', { method: 'GET' }, 'Could not load the quotes');
export const fetchQuote = (id: string) =>
  call<CqQuoteDetail>(`/quotes/${id}`, { method: 'GET' }, 'Could not load that quote');
export const fetchCqSpotlight = () => call<CqSpotlight>('/spotlight', { method: 'GET' }, 'Could not load Spotlight');
export const fetchCqConfiguration = () =>
  call<CqConfiguration>('/configuration', { method: 'GET' }, 'Could not load how the agent is set up');
export const fetchCqIntegrations = () =>
  call<{ integrations: CqIntegration[] }>('/integrations', { method: 'GET' }, 'Could not load integrations');
export const fetchMessages = (carrierQuoteId: string) =>
  call<CqMessages>(`/carrier-quotes/${carrierQuoteId}/messages`, { method: 'GET' }, 'Could not load the messages');

/** A person has looked the customer file over: ask the carriers. */
export const confirmQuote = (id: string, answers: Record<string, string>) =>
  call<CqQuoteDetail>(`/quotes/${id}/confirm`, { method: 'POST', body: JSON.stringify({ answers }) },
    'Could not send the file to the carriers');

/** A carrier's own website, filled in by the agent: submit it. */
export const submitCarrier = (carrierQuoteId: string) =>
  call<CqQuoteDetail>(`/carrier-quotes/${carrierQuoteId}/submit`, { method: 'POST' }, 'Could not submit that carrier');

/** Open something from the server in a new tab, carrying the sign-in with it. */
async function openInTab(path: string, fallback: string): Promise<void> {
  const response = await fetch(buildApiUrl(`${prefix()}${path}`), { headers: authHeaders(false) });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallback);
  }
  const url = URL.createObjectURL(await response.blob());
  window.open(url, '_blank', 'noopener');
  // The tab holds its own copy; this only releases ours.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Put a different carrier or package on the proposal. No carrier: back to the rules' pick. */
export const chooseProposal = (id: string, carrier: string | null, pkg: CqPackageKey | null) =>
  call<CqQuoteDetail>(`/quotes/${id}/proposal`, { method: 'PUT', body: JSON.stringify({ carrier, package: pkg }) },
    'Could not change the proposal');

/** Send the proposal to the customer, with the PDF attached. A real email. */
export const emailProposal = (id: string, email: { to: string; subject: string; body: string }) =>
  call<CqQuoteDetail>(`/quotes/${id}/proposal/email`, { method: 'POST', body: JSON.stringify(email) },
    'Could not send the email');

/** A document's bytes as something an <img> can show, carrying the sign-in. Release it when done. */
export async function documentPicture(id: string): Promise<string> {
  const response = await fetch(buildApiUrl(`${prefix()}/documents/${id}/file`), { headers: authHeaders(false) });
  if (!response.ok) throw new Error('Could not load that picture');
  return URL.createObjectURL(await response.blob());
}

export const openDocument = (id: string) => openInTab(`/documents/${id}/file`, 'Could not open that document');
export const openProposal = (quoteId: string) =>
  openInTab(`/quotes/${quoteId}/proposal.pdf`, 'Could not build the proposal');

/** Upload one household's paperwork. It is read in the background. */
export async function startQuote(files: File[]): Promise<CqQuoteDetail> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const response = await fetch(buildApiUrl(`${prefix()}/quotes`), {
    method: 'POST',
    headers: authHeaders(false),   // the browser sets the multipart boundary itself
    body: form,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Could not upload those documents');
  }
  return response.json();
}

/** Listen for "the quotes changed" until `signal` aborts. Carries no data. */
export async function watchQuotes(onChange: () => void, signal: AbortSignal, onOpen?: () => void): Promise<void> {
  const response = await fetch(buildApiUrl(`${prefix()}/quotes/stream`), {
    headers: { ...authHeaders(), Accept: 'text/event-stream' }, signal,
  });
  if (!response.ok || !response.body) throw new Error(`Live updates unavailable (${response.status})`);
  onOpen?.();
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let end = buffer.indexOf('\n\n');
    while (end !== -1) {
      if (buffer.slice(0, end).split('\n').includes('event: changed')) onChange();
      buffer = buffer.slice(end + 2);
      end = buffer.indexOf('\n\n');
    }
  }
}
